import {
  AfterViewInit,
  Component,
  inject,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { AbstractIdbaModel } from '../../models/abstract-idba-model';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { ExcelService } from '../../services/excel.service';
import { FilterFieldComponent } from '../filter-field/filter-field.component';
import { GenericIdbaComponent } from './generic-idba.component';
import { GenericIdbaService } from '../../services/generic-idba-service.service';
import { GlobalVariablesService } from '../../services/global-variables.service';
import { IdbaTableAction } from '../../models/mat-table-model';
import { JobInfoComponent } from '../job-info/job-info.component';
import { takeUntil } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-idba-list-component',
  template: ``,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class GenericIdbaListComponent extends GenericIdbaComponent implements AfterViewInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild(FilterFieldComponent, { static: true }) filterInput?: FilterFieldComponent;

  public dialogConfig: MatDialogConfig;
  public filterDialog: any;
  public columns: any;
  public displayedColumns: any;
  public rowActions: IdbaTableAction[] = [];
  public loadDataOnInit: boolean = false;
  private _filterObject: any;
  public orderByFunction: any;
  public filterName: string = '';
  public filterKey: string = '';

  public excelService = inject(ExcelService);
  public dialog = inject(MatDialog);
  public globalVariables = inject(GlobalVariablesService);
  public service: GenericIdbaService;

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  ngAfterViewInit(): void {
    this.syncSomenteAtivos();

    this.filterInput?.somenteAtivosChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((somenteAtivos) => this.onSomenteAtivosChange(somenteAtivos));

    if (this.loadDataOnInit) {
      this.updateDataSource();
    }
  }

  override ngOnInit(): void {
    if (!this.programa && this.globalVariables?.telaAtiva) {
      this.programa = this.globalVariables.telaAtiva.replace(' ', '_');
    }
    const filterKey = this.filterKey == '' ? `${this.programa}Filter` : this.filterKey;

    const storedFilter = sessionStorage.getItem(filterKey);
    const parsedFilter = storedFilter ? JSON.parse(storedFilter) : null;
    if (parsedFilter && Object.keys(parsedFilter).length > 0) {
      this.filterObject = parsedFilter;
    } else {
      this.initializeFilter();
      this.updateFilterObject(this.filterObject);
    }

    this.syncSomenteAtivos();

    super.ngOnInit();
  }

  initializeFilter(): void {
    this.filterObject = this.filterInput?.showSomenteAtivos
      ? { somenteAtivos: this.filterInput.somenteAtivos }
      : {};
  }

  private syncSomenteAtivos(): void {
    const somenteAtivos = this.filterObject?.somenteAtivos;

    if (this.filterInput?.showSomenteAtivos && typeof somenteAtivos === 'boolean') {
      this.filterInput.somenteAtivos = somenteAtivos;
    }
  }

  getDisplayedColumns(columns = this.columns) {
    const returnColumns: string[] = [];
    for (let index = 0; index < columns.length; index++) {
      const coluna = columns[index];
      const funcao = this.funcoes[coluna.accessFunction];
      if (funcao === undefined && !coluna.accessFunctionRequired && coluna.display) {
        returnColumns.push(coluna.columnDef);
      } else if (funcao === true && coluna.display) {
        returnColumns.push(coluna.columnDef);
      }
    }

    return returnColumns;
  }

  get filterObject() {
    if (this._filterObject) {
      return this._filterObject;
    } else {
      return {};
    }
  }

  set filterObject(params) {
    this._filterObject = params;
  }

  updateFilterObject(filterObject) {
    if (filterObject) {
      this.filterObject = filterObject;
      const filterKey = this.filterKey == '' ? `${this.programa}Filter` : this.filterKey;

      sessionStorage.setItem(filterKey, JSON.stringify(this.filterObject));
    }
  }

  updateDataSource(afterupdateDataSource: any = undefined) {
    this.service.listAllByFilter(this.getListFilterObject()).then((data) => {
      setTimeout(() => {
        this.dataSource = new MatTableDataSource(data);
        if (this.sort) {
          this.dataSource.sort = this.sort;
        }
        this.dataSource.paginator = this.paginator;

        for (let index = 0; index < this?.columns?.length; index++) {
          const Coluna = this.columns[index];
          if (Coluna?.calculateFooter) {
            Coluna.footer = '0';
            for (let index = 0; index < data?.length; index++) {
              const valor = data[index][Coluna.columnDef];
              Coluna.footer = (parseFloat(Coluna.footer) + parseFloat(valor)).toString();
            }
          }

          if (Coluna?.sort && this.sort) {
            this.sort.active = Coluna.columnDef;
            this.sort.direction = Coluna.sortDirection;
            this.sort.sortChange.emit();
          }
        }

        this.applyFilterAfterLoad();

        if (afterupdateDataSource) {
          afterupdateDataSource();
        }
      });
    });
  }

  public getListFilterObject() {
    const filterObject = { ...this.filterObject };

    if (this.filterInput?.showSomenteAtivos) {
      filterObject.somenteAtivos =
        typeof filterObject.somenteAtivos === 'boolean'
          ? filterObject.somenteAtivos
          : this.filterInput.somenteAtivos;
    } else {
      delete filterObject.somenteAtivos;
    }

    return filterObject;
  }

  public onSomenteAtivosChange(somenteAtivos: boolean): void {
    this.updateFilterObject({
      ...this.filterObject,
      somenteAtivos,
    });

    this.updateDataSource();
  }

  public applyFilterAfterLoad() {
    var filter = sessionStorage.getItem(`filters`);
    if (this.filterInput && filter) {
      var filters = JSON.parse(filter);

      var name = this.filterName == '' ? this.programa : this.filterName;

      if (filters[name]) {
        this.filterInput!.setValue(filters[name]);
        this.applyFilter(filters[name]);
      }
    }
  }

  override consultaPermissaoAcesso() {
    this.acessoService.pesquisaPermissaoAcesso().subscribe({
      next: (response) => {
        this.acesso = response.body.data;
        if (this.acesso?.funcoes) {
          this.acesso.funcoes.forEach((element) => {
            this.funcoes[element.codigo] = element.ativo;
          });
        }
        this.getGenericParams();
      },
    });
  }

  public applyFilter(value: string) {
    this.dataSource.filter = value.trim().toLowerCase();

    var name = this.filterName == '' ? this.programa : this.filterName;
    var filter = sessionStorage.getItem(`filters`)
      ? JSON.parse(sessionStorage.getItem(`filters`)!)
      : {};
    filter[name] = value.trim().toLowerCase();
    sessionStorage.setItem(`filters`, JSON.stringify(filter));
  }

  public gerarPlanilha() {
    let planilha = 'Planilha';

    if (this.dataSource?.data) {
      if (this.programa) {
        planilha = this.programa;
      }

      const colunas = this.columns.filter((element) => (element?.export !== true ? false : true));
      const data = this.dataSource.data;

      if (this.orderByFunction) {
        this.orderByFunction(data);
      }

      this.excelService.gerarPlanilha(
        data,
        colunas.map((element) => element.columnDef),
        colunas.map((element) => element.header),
        planilha,
        `${planilha}.xlsx`,
        colunas,
      );
    }
  }

  public abreFiltros() {
    this.dialogConfig = new MatDialogConfig();
    this.dialogConfig.width = '50%';
    this.dialogConfig.autoFocus = false;
    this.dialogConfig.data = this.filterObject;

    if (this.service.filterComponent) {
      this.filterDialog = this.dialog.open(this.service.filterComponent, this.dialogConfig);
      this.filterDialog.afterClosed().subscribe((result) => {
        if (result) {
          const nextFilter = { ...result };

          if (
            this.filterInput?.showSomenteAtivos &&
            typeof nextFilter.somenteAtivos !== 'boolean'
          ) {
            nextFilter.somenteAtivos = this.filterInput.somenteAtivos;
          }

          this.afterCloseFilter(nextFilter);
          this.filterObject = nextFilter;
          this.updateFilterObject(this.filterObject);
          this.syncSomenteAtivos();
          this.updateDataSource();
        }
      });
    }
  }

  public afterCloseFilter(result) {}

  protected afterDeleteSuccess(element: any): void {}

  public excluir(element: any) {
    const model: AbstractIdbaModel = this.service.model.convertToModel(element);

    const confirmRemoveDialog = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar Exclusão do Registro',
        msg: `Tem certeza que deseja prosseguir com a Exclusão do Registro ${model.display(
          model,
        )}?`,
      },
    });

    confirmRemoveDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.service.excluir(model.rowid).subscribe({
          next: (response) => {
            this.snackBar.openSnackBar(
              `Registro ${model.display(model)} foi excluído com sucesso.`,
              null,
            );
            this.afterDeleteSuccess(element);
            this.updateDataSource();
            this.applyFilterAfterLoad();
          },
        });
      }
    });
  }

  openJobInfo(nomeTabela: string, idTabela: number) {
    var dialogConfig = new MatDialogConfig();
    dialogConfig.maxHeight = '90vh';
    dialogConfig.maxWidth = '90vw';
    dialogConfig.width = '900px';
    dialogConfig.data = { nomeTabela, idTabela };

    this.dialog.open(JobInfoComponent, dialogConfig);
  }
}
